/** Subterranean gates: the surface mouths, the reconciliation that deletes any
 * channel without exactly one mouth per level, and the read-back into
 * `GatePair`s for the finished map.
 */

import type { MapObjectState } from "@game/core";
import { walkable } from "./grid.js";
import { dropObject, place } from "./placement.js";
import { shuffle } from "./rng.js";
import type { GenState } from "./state.js";
import type { GateMouth, GatePair } from "./types.js";

export const GATE = "tp_subterranean_gate";

export const isGate = (o: MapObjectState): boolean => o.def === GATE;

export const channelOf = (o: MapObjectState): string =>
  typeof o.payload["channel"] === "string" ? o.payload["channel"] : "";

/**
 * The surface half of every subterranean gate, opened *before* the audit so a
 * gate has to survive the same reachability test as a resource pile. Returns
 * the channel of each mouth that was placed; the underground pass matches them
 * and drops any it cannot answer.
 */
export function openGateMouths(s: GenState, count: number): string[] {
  const land = s.zones.filter((z) => z.terrain !== "water" && z.cells.length > 40);
  if (land.length === 0) return [];
  const out: string[] = [];
  for (let k = 0; k < count; k++) {
    const channel = `subterra.${String(k)}`;
    // Every zone in turn, not one at random. Thirty attempts in a single zone
    // was enough when a zone held thirty objects; a zone now holds several
    // hundred on a large map, and a crowded one swallowed the mouth silently -
    // leaving a channel with one end, which is an underground a hero can fall
    // into and not climb out of. Seed 8717 came out with one gate pair instead
    // of two exactly this way.
    let placed = false;
    for (const zone of shuffle(s.r, land.slice())) {
      const cells = zone.cells.filter((i) => walkable(s.map, i));
      if (place(s, cells, GATE, () => ({ channel }), 30) >= 0) { placed = true; break; }
    }
    if (placed) out.push(channel);
  }
  return out;
}

/**
 * Remove every mouth of one channel from one level's list. Matched on the def
 * as well as the channel: monoliths carry a `channel` too, and an empty string
 * would otherwise match every object on the map that has no channel at all.
 */
export function dropGates(s: GenState, channel: string): void {
  if (channel === "") return;
  for (let i = s.objects.length - 1; i >= 0; i--) {
    const o = s.objects[i]!;
    if (isGate(o) && channelOf(o) === channel) dropObject(s, i);
  }
}

/**
 * The last word on the gates: a channel that does not have exactly one mouth
 * on each level is deleted from both. A one-sided gate is worse than no gate -
 * `portalExits` would find nothing and the hero would stand on it forever.
 */
export function reconcileGates(s: GenState, u: GenState): void {
  const channels = new Set<string>();
  for (const o of [...s.objects, ...u.objects]) if (isGate(o)) channels.add(channelOf(o));
  const count = (st: GenState, c: string): number =>
    st.objects.filter((o) => isGate(o) && channelOf(o) === c).length;
  for (const c of [...channels].sort()) {
    if (c !== "" && count(s, c) === 1 && count(u, c) === 1) continue;
    dropGates(s, c);
    dropGates(u, c);
  }
}

/** Read the finished object list back as gate pairs. */
export function gatePairs(objects: readonly MapObjectState[]): GatePair[] {
  const byChannel = new Map<string, GateMouth[]>();
  for (let i = 0; i < objects.length; i++) {
    const o = objects[i]!;
    if (!isGate(o)) continue;
    const channel = channelOf(o);
    if (channel === "") continue;
    const list = byChannel.get(channel) ?? [];
    list.push({ x: o.x, y: o.y, z: o.z, object: i });
    byChannel.set(channel, list);
  }
  const out: GatePair[] = [];
  for (const channel of [...byChannel.keys()].sort()) {
    const mouths = byChannel.get(channel) ?? [];
    const surface = mouths.find((m) => m.z === 0);
    const under = mouths.find((m) => m.z === 1);
    if (mouths.length !== 2 || surface === undefined || under === undefined) continue;
    out.push({ channel, surface, underground: under });
  }
  return out;
}
